import * as React from "react";
import { fileUploadStore, UploadedTmpFile, UploadMode } from "./FileUploadStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Progress, Tooltip, Result, Tag } from "antd";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { UploadFileStatus } from "antd/lib/upload/interface";
import { withTranslation, WithTranslation, useTranslation } from "react-i18next";
import { AutoSizer as _AutoSizer, List as _List, AutoSizerProps, ListProps } from "react-virtualized";
import { I18N_NS } from "Config/i18n";
import { breadcrumbsStore } from "../Stores/BreadcrumbsStore";
import { EllipsisLeft } from "Components/General/EllipsisLeft";
import { ProgressProps } from "antd/lib/progress";
import classNames from "classnames";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { TreeZipData, ZipArchiveContentModal } from "./ArchiveContentModal";
import Title from "antd/lib/typography/Title";
import { ModalProps } from "antd/lib";
const AutoSizer = _AutoSizer as unknown as React.FC<AutoSizerProps>;
const List = _List as unknown as React.FC<ListProps>;

interface FileUploadListProps {
  files: UploadedTmpFile[];
  directoriesCreated: boolean;
  onRemove: (id: string | number) => void;
  onExtract?: (mode: API_ARCHIVEDOBJECT_EXTRACTMODE, id: number) => Promise<void>;
  pathNameInfo?: boolean;
}
const fileUploadStatusIconMap: { [key in UploadFileStatus]?: { color: string; icon: IconProp } } = {
  done: { color: "#52c41a", icon: ["fas", "check-circle"] },
  error: { color: "#ff8145", icon: ["fas", "exclamation-circle"] },
};

const fileStatusToProgressStatus: { [key in UploadFileStatus]?: Pick<ProgressProps, "status">["status"] } = {
  done: "success",
  error: "exception",
  uploading: "active",
};

interface FileUploadStatusIconProps extends Pick<UploadedTmpFile, "status">, WithTranslation {
  hasError: boolean;
}

const FileUploadStatusIcon = withTranslation(I18N_NS.COMPONENT_UPLOAD)(
  ({ status, t, hasError }: FileUploadStatusIconProps) => {
    if (!fileUploadStatusIconMap[status]) {
      return null;
    }

    if (fileUploadStore.module === "VIEW" && fileUploadStore.getFilesByErrorStatus("VIEW_NO_AUTOMAPPING").length) {
      const hint = "This file cannot be automapped, therefore an upload is not allowed";
      return (
        <Tooltip title={t(hint)}>
          <Tag style={{ marginRight: 0, marginLeft: 4 }} color="orange">
            STOP
          </Tag>
        </Tooltip>
      );
    }

    if (hasError || status === "error") {
      const hint = hasError ? "Detected problems can be resolved in the next step" : "Error ocurred during file upload";
      return (
        <Tooltip title={t(hint)}>
          <FontAwesomeIcon icon={fileUploadStatusIconMap.error.icon} color={fileUploadStatusIconMap.error.color} />
        </Tooltip>
      );
    }

    return (
      <FontAwesomeIcon icon={fileUploadStatusIconMap[status].icon} color={fileUploadStatusIconMap[status].color} />
    );
  }
);

interface FileUploadItemProps {
  file: UploadedTmpFile;
  onRemove: (id: string | number) => void;
  onExtract: (mode: API_ARCHIVEDOBJECT_EXTRACTMODE, id: number) => Promise<void>;
  isArchive?: boolean;
}

const FileUploadItem = ({ file, onRemove, onExtract, isArchive }: FileUploadItemProps) => {
  const classes = classNames("upload-file-name text-ellipsis mr-md", { underline: isArchive });
  const MODAL_PROPS: ModalProps = {
    width: `100%`,
    style: { maxWidth: 840, top: 100 },
    footer: null,
    // zIndex: 99999,
    getContainer: () => document.getElementById("upload-modal-body"),
  };
  const { t } = useTranslation(I18N_NS.PROJECT);

  return (
    <div className="upload-file">
      <div>
        <FontAwesomeIcon icon={["far", "paperclip"]} fixedWidth={true} className="mr-5" />
        <span className={classes}>{file.name}</span>
        {isArchive && (
          <ModalConsumer>
            {({ showModal }) => (
              <span
                className="mr-5 pointer"
                onClick={() =>
                  showModal(ZipArchiveContentModal, {
                    modalProps: {
                      ...MODAL_PROPS,
                      title: file.name,
                    },
                    componentProps: {
                      treeData: file.archivedTree as TreeZipData[],
                      onExtract,
                      fileId: file.id,
                      extractModes: file.extractModes,
                    },
                  })
                }
              >
                <FontAwesomeIcon icon={["far", "file-archive"]} fixedWidth={true} className="mr-5" />
                <span>{t("ZIP Options")}</span>
              </span>
            )}
          </ModalConsumer>
        )}
        <div className="upload-status">
          <Progress percent={file.percent} showInfo={false} status={fileStatusToProgressStatus[file.status]} />
          <div className="upload-file-icon">
            <FileUploadStatusIcon status={file.status} hasError={!!file.errors} />
          </div>
          <span className="upload-file-icon pointer" onClick={() => onRemove(file.id || file.uid)}>
            <FontAwesomeIcon icon={["far", "times"]} fixedWidth={true} />
          </span>
        </div>
      </div>
    </div>
  );
};

export const FileUploadList = ({
  files,
  onRemove,
  onExtract,
  pathNameInfo = true,
  directoriesCreated,
}: FileUploadListProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);

  const getUploadInfo = (mode: UploadMode) => {
    const infoMap: { [key in UploadMode]?: string | React.ReactNode } = {
      VIEW: t("Files will be automapped and added to all matching views"),
      WORKFLOW: t(
        `Files will be uploaded into the workflow only, not into the Document module. Depending on the workflow configuration there can be the possibilty to move workflow files into the Documents module later on (e.g. as final step of a review).`
      ),
      DOCUMENTS: <EllipsisLeft color="mid-grey" maxWidth={1680} text={breadcrumbsStore.currentPathname} />,
    };
    return infoMap[mode];
  };

  if (pathNameInfo && !files.length) {
    return (
      <div className="upload-file-list color-mid-grey">
        <Result
          status="info"
          icon={
            <Tooltip
              title={t(
                `Files will be uploaded to current\n directory, or, if a directory\n mapping by name is defined, to\n a defined target directory`
              )}
            >
              <FontAwesomeIcon icon={["far", "info-circle"]} size="5x" />
            </Tooltip>
          }
          title={
            fileUploadStore.module === "DOCUMENTS" && <span className="color-mid-grey">{t("Current directory:")}</span>
          }
          subTitle={
            <div className="color-mid-grey" style={{ display: "flex", justifyContent: "center" }}>
              {getUploadInfo(fileUploadStore.module)}
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className="upload-file-list">
      {directoriesCreated ? (
        <Title level={3} className="text-center mt-2">
          {t("ZIP-Directories were created")}
        </Title>
      ) : null}
      <AutoSizer>
        {({ width, height }) => (
          <List
            width={width}
            height={height}
            rowCount={files.length}
            rowHeight={50}
            rowRenderer={({ index, key, style }) => {
              const isArchive = files[index].archive;

              return (
                <div style={style} key={key}>
                  <FileUploadItem onExtract={onExtract} file={files[index]} onRemove={onRemove} isArchive={isArchive} />
                </div>
              );
            }}
          />
        )}
      </AutoSizer>
    </div>
  );
};
